import { Peer } from "peerjs";

const ChatManager = {
    container: null,
    messagesEl: null,
    inputEl: null,
    room: null,
    isHidden: false,
    unreadCount: 0,
    badgeEl: null,
    micBtnEl: null,
    standaloneMicBtnEl: null, // [NEW] Standalone mic button for mobile
    isMobileMicOnly: false, // [NEW] Mode flag
    isPTTActive: false,
    pttKeys: new Set(),

    init: function (room, options = {}) {
        this.room = room;
        // [FIX] Set initial state BEFORE render
        this.isHidden = options.startHidden || false;

        // Always append to body for safety
        this.render(document.body);
        this.setupListeners();
        this.badgeEl = document.getElementById('chat-notification-badge'); // [NEW] Cache Badge
        this.initVoice(); // [NEW] Start Voice Logic

        // [NEW] Click Outside Logic
        document.addEventListener('mousedown', (e) => {
            if (!this.container || this.isHidden) return;

            // Check if click is inside container OR on the toggle button
            const isInside = this.container.contains(e.target);
            const isToggleBtn = e.target.closest('#header-chat-btn'); // ID from OnlineManager logic

            if (!isInside && !isToggleBtn) {
                // If input is focused, maybe keep it? User said "click outside -> disappears"
                this.toggleMinimize(true);
            }
        });

        // [NEW] Global Push-to-Talk Hotkey Listener
        document.addEventListener('keydown', (e) => this.handlePTTEvent(e));
        document.addEventListener('keyup', (e) => this.handlePTTEvent(e));
    },

    startDrag: function (e, element) {
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        const rect = element.getBoundingClientRect();
        const offsetX = startX - rect.left;
        const offsetY = startY - rect.top;

        const onMouseMove = (moveEvent) => {
            let newLeft = moveEvent.clientX - offsetX;
            let newTop = moveEvent.clientY - offsetY;

            element.style.left = `${newLeft}px`;
            element.style.top = `${newTop}px`;
            element.style.right = 'auto';
            element.style.bottom = 'auto';
            element.style.transform = 'none';
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'default';
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.body.style.cursor = 'grabbing';
    },

    render: function (parent) {
        // Remove existing if any
        const existing = document.getElementById('online-chat-container');
        if (existing) existing.remove();

        // Container - GLASSMORPHISM STYLE
        this.container = document.createElement('div');
        this.container.id = 'online-chat-container';
        Object.assign(this.container.style, {
            position: 'fixed',
            right: 'max(20px, env(safe-area-inset-right, 10px))',
            bottom: 'max(80px, env(safe-area-inset-bottom, 20px))', // Higher on mobile to avoid overlap
            width: 'min(360px, 92vw)', // Responsive width
            height: 'min(500px, 60vh)', // Responsive height (avoid keyboard overlap)
            background: 'rgba(20, 20, 20, 0.65)', // Semi-transparent black
            backdropFilter: 'blur(12px)', // Glass effect
            webkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 215, 0, 0.3)', // Subtle gold border
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: '99999', // [MODIFIED] Higher for result screen
            fontFamily: "'Outfit', sans-serif", // Premium font
            overflow: 'hidden',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5), 0 0 10px rgba(255, 215, 0, 0.1)', // Modern float shadow
            transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            // [FIX] Initial State based on isHidden
            opacity: this.isHidden ? '0' : '1',
            transform: this.isHidden ? 'scale(0.9)' : 'scale(1)',
            pointerEvents: this.isHidden ? 'none' : 'all'
        });

        // Apply saved position immediately if exists
        if (window.state && window.state.chatPosition === 'left') {
            this.container.style.left = '20px';
            this.container.style.right = 'auto';
        }

        // Header
        const header = document.createElement('div');
        Object.assign(header.style, {
            padding: '15px 20px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#fff',
            cursor: 'grab',
            userSelect: 'none'
        });

        // Header Content
        const titleArea = document.createElement('div');
        titleArea.style.display = 'flex';
        titleArea.style.alignItems = 'center';
        titleArea.style.gap = '10px';
        titleArea.innerHTML = `
            <span style="font-size:1.2rem;">💬</span>
            <div>
                <span style="display:block; font-weight:700; font-size:1rem; letter-spacing:0.5px;">Sohbet</span>
                <span style="display:block; font-size:0.7rem; color:rgba(255,255,255,0.5);">Gaddar Online</span>
            </div>
        `;

        // Actions
        const actionsArea = document.createElement('div');
        actionsArea.style.display = 'flex';
        actionsArea.style.gap = '8px';

        // Close Button (Requested X)
        const closeBtn = document.createElement('div');
        closeBtn.innerHTML = '✕';
        Object.assign(closeBtn.style, {
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.8rem',
            transition: '0.2s'
        });
        closeBtn.onmouseenter = () => closeBtn.style.background = 'rgba(231, 76, 60, 0.8)'; // Red on hover
        closeBtn.onmouseleave = () => closeBtn.style.background = 'rgba(255,255,255,0.1)';
        closeBtn.onclick = (e) => {
            e.stopPropagation();
            this.toggleMinimize(true); // True = Force Hide
        };

        header.onmousedown = (e) => this.startDrag(e, this.container);

        actionsArea.appendChild(closeBtn);
        header.appendChild(titleArea);
        header.appendChild(actionsArea);
        this.container.appendChild(header);

        // Messages Area
        this.messagesEl = document.createElement('div');
        this.messagesEl.id = 'chat-messages';
        Object.assign(this.messagesEl.style, {
            flex: '1',
            padding: '15px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            scrollBehavior: 'smooth',
            fontSize: '0.95rem'
        });
        // Scrollbar styling (Webkit)
        const style = document.createElement('style');
        style.innerHTML = `
            #chat-messages::-webkit-scrollbar { width: 6px; }
            #chat-messages::-webkit-scrollbar-track { background: transparent; }
            #chat-messages::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
            #chat-messages::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
            @keyframes pulse {
                0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7); }
                70% { transform: scale(1.1); box-shadow: 0 0 0 15px rgba(231, 76, 60, 0); }
                100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(231, 76, 60, 0); }
            }
        `;
        this.container.appendChild(style);
        this.container.appendChild(this.messagesEl);

        // [FIX] Apply Accessibility Font Size
        if (window.state && window.state.chatFontSize) {
            this.messagesEl.style.fontSize = window.state.chatFontSize;
        } else {
            const savedSize = localStorage.getItem('gaddar_chat_size');
            if (savedSize) {
                this.messagesEl.style.fontSize = savedSize.endsWith('pt') ? savedSize : (savedSize + 'pt');
            }
        }

        // Input Area
        const inputContainer = document.createElement('div');
        Object.assign(inputContainer.style, {
            padding: '15px',
            background: 'rgba(0,0,0,0.2)',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        });

        this.inputEl = document.createElement('input');
        Object.assign(this.inputEl.style, {
            flex: '1',
            padding: '12px 15px',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            color: '#fff',
            outline: 'none',
            fontSize: '0.9rem',
            transition: '0.3s',
            fontFamily: "'Outfit', sans-serif"
        });
        this.inputEl.placeholder = "Bir şeyler yaz...";
        this.inputEl.onfocus = () => {
            this.inputEl.style.background = 'rgba(255,255,255,0.1)';
            this.inputEl.style.borderColor = 'var(--accent-gold)';
        };
        this.inputEl.onblur = () => {
            this.inputEl.style.background = 'rgba(255,255,255,0.05)';
            this.inputEl.style.borderColor = 'rgba(255,255,255,0.1)';
        };
        this.inputEl.onkeydown = (e) => {
            if (e.key === 'Enter') this.sendMessage();
            e.stopPropagation();
        };

        const sendBtn = document.createElement('button');
        sendBtn.innerHTML = '<span style="font-size:1.2rem;">➤</span>';
        Object.assign(sendBtn.style, {
            background: 'var(--accent-gold)',
            border: 'none',
            borderRadius: '12px',
            width: '45px',
            height: '42px',
            cursor: 'pointer',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: '0.2s',
            boxShadow: '0 0 10px rgba(255, 215, 0, 0.2)'
        });
        sendBtn.onmouseenter = () => sendBtn.style.transform = 'scale(1.05)';
        sendBtn.onmouseleave = () => sendBtn.style.transform = 'scale(1)';
        sendBtn.onclick = () => this.sendMessage();

        // Mic Button
        const micBtn = document.createElement('button');
        micBtn.innerHTML = '<span style="font-size:1.2rem;">🔇</span>';
        Object.assign(micBtn.style, {
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '12px',
            width: '45px',
            height: '42px',
            cursor: 'pointer',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: '0.2s',
            boxShadow: '0 0 5px rgba(0,0,0,0.2)'
        });
        micBtn.onclick = () => this.toggleMic();
        this.micBtnEl = micBtn; // Save reference

        inputContainer.appendChild(micBtn); // Add Mic Button [First]
        inputContainer.appendChild(this.inputEl);
        inputContainer.appendChild(sendBtn);
        this.container.appendChild(inputContainer);

        // [NEW] STANDALONE MIC BUTTON FOR MOBILE
        this.renderStandaloneMic(parent);

        parent.appendChild(this.container);
    },

    renderStandaloneMic: function (parent) {
        if (this.standaloneMicBtnEl) this.standaloneMicBtnEl.remove();

        this.standaloneMicBtnEl = document.createElement('button');
        this.standaloneMicBtnEl.id = 'standalone-mic-btn';
        this.standaloneMicBtnEl.innerHTML = '<span style="font-size:1.5rem;">🔇</span>';
        Object.assign(this.standaloneMicBtnEl.style, {
            position: 'fixed',
            right: '25px',
            bottom: '100px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'rgba(20, 20, 20, 0.65)',
            backdropFilter: 'blur(12px)',
            webkitBackdropFilter: 'blur(12px)',
            border: '2px solid rgba(255, 215, 0, 0.5)',
            color: '#fff',
            cursor: 'pointer',
            zIndex: '100000',
            display: 'none', // Hidden by default
            alignItems: 'center',
            justifyContent: 'center',
            transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
        });

        this.standaloneMicBtnEl.onclick = () => this.toggleMic();
        parent.appendChild(this.standaloneMicBtnEl);
    },

    setMobileMicOnly: function (enabled) {
        this.isMobileMicOnly = enabled;
        if (this.container) {
            this.container.style.display = enabled ? 'none' : 'flex';
        }
        if (this.standaloneMicBtnEl) {
            this.standaloneMicBtnEl.style.display = enabled ? 'flex' : 'none';
        }

        // Also hide header chat button if enabled
        const chatBtn = document.getElementById('header-chat-btn');
        if (chatBtn) {
            chatBtn.style.display = enabled ? 'none' : 'flex';
        }
    },

    updateBadge: function () {
        if (!this.badgeEl) return;
        this.badgeEl.innerText = this.unreadCount;
        this.badgeEl.style.display = this.unreadCount > 0 ? 'flex' : 'none';

        // Pop Animation
        if (this.unreadCount > 0) {
            this.badgeEl.style.transform = 'scale(1.3)';
            this.badgeEl.style.transition = 'transform 0.1s ease-out';
            setTimeout(() => {
                this.badgeEl.style.transform = 'scale(1)';
            }, 100);
        }
    },

    setupListeners: function () {
        if (!this.room) return;

        // [FIX] Prevent Duplicate Listeners
        if (this.currentListenerRoomId === this.room.sessionId) {
            console.log("ChatManager: Listeners already active for this room/session.");
            return;
        }
        this.currentListenerRoomId = this.room.sessionId;

        console.log("ChatManager: Setting up chat listeners...");

        this.room.onMessage("chat", (data) => {
            this.addMessage(data);

            // [NEW] Auto-open chat if hidden and message is from others
            // [NEW] Notification Badge Logic
            const isMe = (this.room && this.room.sessionId === data.senderId);
            if (this.isHidden && !isMe) {
                // this.toggleMinimize(false); // REMOVED Auto-open
                this.unreadCount++;
                this.updateBadge();
            }

            // [SOUND] Logic
            if (window.audio && window.audio.playSFX) {
                window.audio.playSFX('mesaj.mp3');
            } else {
                const audio = new Audio(new URL('../../assets/SFX/mesaj.mp3', import.meta.url).href);
                audio.volume = (window.audio && window.audio.sfxVolume !== undefined) ? window.audio.sfxVolume : 0.5;
                audio.play().catch(e => console.warn("Chat sound prevented:", e));
            }
        });
    },

    sendMessage: function () {
        const text = this.inputEl.value.trim();
        if (text.length > 0 && this.room) {
            this.room.send("chat", text);
            this.inputEl.value = '';
            this.inputEl.focus();
        }
    },

    addMessage: function (data) {
        console.log("💬 ChatManager: Received Message", data);
        if (!this.messagesEl) return;

        const msgWrapper = document.createElement('div');
        const isMe = (this.room && this.room.sessionId === data.senderId);

        Object.assign(msgWrapper.style, {
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMe ? 'flex-end' : 'flex-start',
            marginBottom: '5px'
        });

        // Name Tag
        if (!isMe) {
            const nameSpan = document.createElement('div');
            nameSpan.innerText = data.senderName;
            nameSpan.style.fontSize = '0.95rem'; // [MODIFIED] Increased from 0.7rem
            nameSpan.style.fontWeight = 'bold'; // [NEW] Added bold for better visibility
            nameSpan.style.color = 'rgba(255,255,255,0.8)'; // [MODIFIED] Slightly lighter for contrast
            nameSpan.style.marginBottom = '2px';
            nameSpan.style.marginLeft = '5px';
            msgWrapper.appendChild(nameSpan);
        }

        // Bubble
        const msgBubble = document.createElement('div');
        Object.assign(msgBubble.style, {
            padding: '10px 14px',
            borderRadius: isMe ? '15px 15px 2px 15px' : '15px 15px 15px 2px',
            background: isMe ? 'linear-gradient(135deg, var(--accent-gold), #f39c12)' : 'rgba(255, 255, 255, 0.1)',
            color: isMe ? '#000' : '#fff',
            maxWidth: '85%',
            wordWrap: 'break-word',
            fontSize: 'inherit',
            lineHeight: '1.4',
            boxShadow: isMe ? '0 4px 10px rgba(241, 196, 15, 0.2)' : 'none',
            fontWeight: isMe ? '600' : '400',
            border: isMe ? 'none' : '1px solid rgba(255,255,255,0.1)'
        });
        msgBubble.innerText = data.text;

        msgWrapper.appendChild(msgBubble);
        this.messagesEl.appendChild(msgWrapper);

        // Auto scroll with animation
        setTimeout(() => {
            this.messagesEl.scrollTo({ top: this.messagesEl.scrollHeight, behavior: 'smooth' });
        }, 50);
    },

    toggleMinimize: function (forceHide = false) {
        if (forceHide) {
            this.isHidden = true;
        } else {
            this.isHidden = !this.isHidden;
        }

        if (this.isHidden) {
            // Full Hide (Opacity + Scale)
            this.container.style.opacity = '0';
            this.container.style.transform = 'scale(0.9)';
            this.container.style.pointerEvents = 'none'; // Prevent clicks while hidden
        } else {
            // Show
            this.container.style.opacity = '1';
            this.container.style.transform = 'scale(1)';
            this.container.style.pointerEvents = 'all';

            // [NEW] Reset Badge on Open
            this.unreadCount = 0;
            this.updateBadge();
        }
    },

    setPosition: function (pos) {
        if (!this.container) return;
        this.container.style.top = 'auto';
        this.container.style.bottom = '25px';
        this.container.style.transform = this.isHidden ? 'scale(0.9)' : 'scale(1)'; // Preserve transform

        if (pos === 'left') {
            this.container.style.left = '20px';
            this.container.style.right = 'auto';
        } else {
            this.container.style.right = '20px';
            this.container.style.left = 'auto';
        }
    },

    updateFontSize: function (sizePt) {
        if (this.messagesEl) {
            this.messagesEl.style.fontSize = sizePt;
        }
        this.fontSize = sizePt;
    },

    // --- VOICE CHAT LOGIC ---
    myPeer: null,
    myStream: null,
    activeCalls: {}, // Map<peerId, MediaConnection> - Outgoing/Active calls
    knownPeers: new Set(), // Set<peerId> - List of potential peers in room
    isMicOn: false,
    initVoice: function () {
        // ... (rest of code)
        // Removed window.Peer check since we import it
        try {
            // 1. Create Peer with STUN servers for NAT traversal
            this.myPeer = new Peer(undefined, {
                config: {
                    'iceServers': [
                        { urls: 'stun:stun.l.google.com:19302' },
                        { urls: 'stun:stun1.l.google.com:19302' },
                        { urls: 'stun:stun2.l.google.com:19302' }
                    ]
                }
            });
        } catch (e) {
            console.error("Peer init error:", e);
            return;
        }

        this.myPeer.on('open', (id) => {
            console.log('🎤 My Peer ID:', id);
            // Send my Peer ID to the room
            if (this.room) {
                this.room.send("voiceInit", id);
            }
        });

        // 2. Handle Incoming Calls (Other people calling ME)
        this.myPeer.on('call', (call) => {
            console.log("📞 Incoming call from:", call.peer);
            // Answer with my stream (if active) or null.
            // Even if I don't have mic, I must answer to HEAR them.
            call.answer(this.myStream); // can be null/undefined

            call.on('stream', (userStream) => {
                console.log("🔊 Receiving Stream from:", call.peer);
                this.addAudioStream(call.peer, userStream);
            });
            call.on('error', (err) => console.error("Call error:", err));

            // Track this call so we don't duplicate
            this.activeCalls[call.peer] = call;
        });

        this.myPeer.on('error', (err) => console.error("Peer Error:", err));

        // 3. Listen for other peers from Server
        if (this.room) {
            this.room.onMessage("voiceNewPeer", (data) => {
                // data: { sessionId, peerId }
                if (data.sessionId !== this.room.sessionId) {
                    console.log("👤 New Peer Added:", data.peerId);
                    this.knownPeers.add(data.peerId);
                    // If my mic is ON, call them immediately
                    if (this.isMicOn) {
                        this.connectToPeer(data.peerId);
                    }
                }
            });

            this.room.onMessage("voicePeerList", (list) => {
                // list: [{sessionId, peerId}, ...]
                list.forEach(p => {
                    if (p.sessionId !== this.room.sessionId) {
                        this.knownPeers.add(p.peerId);
                    }
                });
                console.log("👥 Known Peers Updated:", this.knownPeers);

                // If my mic is ON, make sure I'm connected to everyone
                if (this.isMicOn) {
                    this.knownPeers.forEach(peerId => this.connectToPeer(peerId));
                }
            });

            // Request list initially
            this.room.send("requestPeerList");

            this.room.onMessage("voicePlayerLeft", (data) => {
                console.log("👋 Voice Peer Left:", data.peerId);
                this.knownPeers.delete(data.peerId);
                // The 'close' event on the call handles stream removal, but we can force it here too if needed
                this.removeAudioStream(data.peerId);
            });
        }
    },

    handlePTTEvent: function (e) {
        // Only trigger if focus is NOT in an input/textarea (unless it's our chat input)
        const isInput = (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');
        if (isInput && e.target !== this.inputEl) return;

        // Check for CTRL + D
        if (e.ctrlKey && (e.key.toLowerCase() === 'd' || e.code === 'KeyD')) {
            e.preventDefault(); // Stop Bookmark dialog

            if (e.type === 'keydown') {
                if (!this.isMicOn && !this.isPTTActive) {
                    console.log("⌨️ PTT: Hotkey pressed. Opening Mic...");
                    this.isPTTActive = true;
                    this.toggleMic();
                }
            }
        }

        // Keyup logic
        if (e.type === 'keyup' && (e.key.toLowerCase() === 'd' || e.code === 'KeyD')) {
            if (this.isPTTActive && this.isMicOn) {
                console.log("⌨️ PTT: Hotkey released. Closing Mic...");
                this.isPTTActive = false;
                this.toggleMic();
            }
        }
    },

    toggleMic: async function (btnOverride) {
        const btn = btnOverride || this.micBtnEl;
        const sBtn = this.standaloneMicBtnEl;

        const updateVisuals = (isOn) => {
            const micHtml = isOn ? '<span style="font-size:1.2rem;">🎙️</span>' : '<span style="font-size:1.2rem;">🔇</span>';
            const sMicHtml = isOn ? '<span style="font-size:1.5rem;">🎙️</span>' : '<span style="font-size:1.5rem;">🔇</span>';
            const bg = isOn ? '#e74c3c' : 'rgba(255,255,255,0.1)';
            const sBg = isOn ? '#e74c3c' : 'rgba(20, 20, 20, 0.65)';
            const anim = isOn ? 'pulse 1.5s infinite' : 'none';

            if (btn) {
                btn.innerHTML = micHtml;
                btn.style.background = bg;
                btn.style.animation = anim;
            }
            if (sBtn) {
                sBtn.innerHTML = sMicHtml;
                sBtn.style.background = sBg;
                sBtn.style.animation = anim;
            }
        };

        if (this.isMicOn) {
            // Turn OFF
            console.log("🎤 Muting Mic...");
            if (this.myStream) {
                this.myStream.getTracks().forEach(track => {
                    track.stop();
                    track.dispatchEvent(new Event('ended'));
                });
                this.myStream = null;
            }
            this.isMicOn = false;
            updateVisuals(false);

            Object.values(this.activeCalls).forEach(call => {
                if (call.peerConnection) {
                    const senders = call.peerConnection.getSenders();
                    senders.forEach(sender => {
                        if (sender.track) sender.track.stop();
                    });
                }
                call.close();
            });
            this.activeCalls = {};

        } else {
            // Turn ON
            try {
                console.log("🎤 Requesting Mic Access...");
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                this.myStream = stream;
                this.isMicOn = true;
                updateVisuals(true);

                // Call Everyone I know!
                console.log("🎤 Mic Live. Calling known peers:", this.knownPeers);
                this.knownPeers.forEach(peerId => {
                    this.connectToPeer(peerId);
                });

            } catch (err) {
                console.error("Mic Access Error:", err);
                alert("Mikrofona erişilemedi! Lütfen tarayıcı izinlerini kontrol edin.");
            }
        }
    },

    connectToPeer: function (remotePeerId) {
        if (!this.myPeer) return;
        if (!this.myStream) return; // Don't call if I have nothing to say

        // Check if already calling?
        if (this.activeCalls[remotePeerId] && this.activeCalls[remotePeerId].open) {
            console.log("⚠️ Already connected to:", remotePeerId);
            // Optional: Check if the existing call has my current stream. 
            // Simplest: Close and Re-call.
            this.activeCalls[remotePeerId].close();
        }

        console.log("📞 Dialing peer:", remotePeerId);

        try {
            const call = this.myPeer.call(remotePeerId, this.myStream);

            if (call) {
                this.activeCalls[remotePeerId] = call;

                // If they answer with a stream (because they also have mic on), we receive it here
                call.on('stream', (remoteStream) => {
                    console.log("🔊 (Call Back) Receiving Stream from:", remotePeerId);
                    this.addAudioStream(remotePeerId, remoteStream);
                });

                call.on('error', (err) => {
                    console.error("Call Connect Error:", err);
                    delete this.activeCalls[remotePeerId];
                });

                call.on('close', () => {
                    console.log("Call closed:", remotePeerId);
                    this.removeAudioStream(remotePeerId);
                    delete this.activeCalls[remotePeerId];
                });
            }
        } catch (e) {
            console.error("Peer call exception:", e);
        }
    },

    addAudioStream: function (peerId, stream) {
        const existingAudio = document.getElementById(`audio-${peerId}`);
        if (existingAudio) {
            // Update stream if exists (e.g. they toggled mic)
            existingAudio.srcObject = stream;
            return;
        }

        const audio = document.createElement('audio');
        audio.id = `audio-${peerId}`;
        audio.srcObject = stream;
        audio.autoplay = true;
        audio.playsInline = true; // Important for mobile
        audio.style.display = 'none';
        this.container.appendChild(audio);
        console.log(`🔊 Audio element created for ${peerId}`);

        // Mobile Safari Auto-Play workaround check
        audio.play().catch(e => {
            console.warn("Audio autoplay blocked (expected on some browsers until interaction):", e);
        });
    },

    removeAudioStream: function (peerId) {
        const audio = document.getElementById(`audio-${peerId}`);
        if (audio) audio.remove();
    },

    destroy: function () {
        if (this.container) this.container.remove();
        if (this.standaloneMicBtnEl) this.standaloneMicBtnEl.remove();

        // Voice Cleanup
        if (this.myStream) {
            this.myStream.getTracks().forEach(track => track.stop());
        }
        if (this.myPeer) {
            this.myPeer.destroy();
        }
        this.activeCalls = {};
        this.knownPeers = new Set();
        this.isMicOn = false;

        this.container = null;
        this.room = null;
    }
};

window.ChatManager = ChatManager;
